import { useApi } from "../../../swr/useApi";
import { api } from "../../api_client";

export interface WarrantyProductSummary {
  id: number;
  name: string;
  price: number | null;
  warrantyId: number | null;
  warrantyTypeId: number | null;
  warrantyProviderId: number | null;
  description: string | null;
  /** Products currently listing this warranty in compatible_warranty_ids. */
  productUsageCount?: number;
  /** Confirmed sale-order lines that reference this warranty. Informational. */
  orderUsageCount?: number;
}

export interface CreateMyWarrantyProductInput {
  name: string;
  price: number;
  warrantyId?: number;
  warrantyTypeId?: number;
  warrantyProviderId?: number;
  description?: string;
}

const BASE = "/api/v1/my-warranties";
const SWR_KEY = "my-warranties";

/**
 * Lists the current seller's warranty SKUs. Backed by the seller-scoped
 * /my-warranties endpoint — never returns warranties owned by other sellers.
 *
 * The generated API client doesn't expose this until `npm run generate:api`
 * runs after the BE module ships.
 */
export function useMyWarrantyProducts() {
  // useApi expects an axios-style service that returns AxiosResponse — it
  // unwraps `.data` internally. Returning `api.get(...)` directly is the
  // shape it wants; previously we awaited+unwrapped here, which made useApi
  // try to read `.data.data` and produced an undefined list.
  return useApi<WarrantyProductSummary[]>(SWR_KEY, () =>
    api.get<WarrantyProductSummary[]>(BASE),
  );
}

export async function createMyWarrantyProduct(
  input: CreateMyWarrantyProductInput,
): Promise<WarrantyProductSummary> {
  const { data } = await api.post<WarrantyProductSummary>(BASE, input);
  return data;
}

export async function updateMyWarrantyProduct(
  id: number,
  input: Partial<CreateMyWarrantyProductInput>,
): Promise<WarrantyProductSummary> {
  const { data } = await api.patch<WarrantyProductSummary>(`${BASE}/${id}`, input);
  return data;
}

export async function deleteMyWarrantyProduct(id: number): Promise<void> {
  await api.delete(`${BASE}/${id}`);
}

export const MY_WARRANTIES_SWR_KEY = SWR_KEY;
